import * as Yup from 'yup';
import {
  UsersIconFilled,
  EnvelopIcon,
  PhoneIcon,
  UserIcon,
} from '@rsos/assets/icons';
import { EventShare } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { isValidEmail } from '@rsos/utils/validators';

export interface MultiInputTag {
  id: string;
  value: string;
  isValid: boolean;
  type?: string;
  error?: string;
}

/**
 *
 * @param share - The event share object
 * @returns The total number of recipients
 */
export const getEventShareCount = (share: EventShare) => {
  return (
    share.recipients.emails.length +
    share.recipients.phone_numbers.length +
    share.recipients.organization_contacts.length +
    share.recipients.contact_groups.length
  );
};

/**
 * Get the display name for the status
 * @param status - The status of the event share
 * @returns The display name for the status
 */
export const getStatusDisplayName = (status: string) => {
  return status === 'active'
    ? 'In Progress'
    : status === 'inactive'
      ? 'Cancelled'
      : 'Expired';
};

export interface RecipientTag {
  entity: string;
  type: 'email' | 'phone' | 'contact' | 'group';
  shareKey?: string; // Required for emails, phones, contacts. Groups don't have share keys.
}
/** Normalize the recipients tags for the event share into a single array
 * @param share - The event share object
 * @returns The normalized recipients tags
 */
export const getRecipientsTags = (share: EventShare): RecipientTag[] => {
  const allRecipients: RecipientTag[] = [
    ...share.recipients.emails.map(email => ({
      entity: email.email,
      type: 'email' as const,
      shareKey: email.share_key,
    })),
    ...share.recipients.phone_numbers.map(phone => ({
      entity: phone.phone_number,
      type: 'phone' as const,
      shareKey: phone.share_key,
    })),
    ...share.recipients.organization_contacts.map(contact => ({
      entity: contact.name,
      type: 'contact' as const,
      shareKey: contact.share_key,
    })),
    ...share.recipients.contact_groups.map(group => ({
      entity: group,
      type: 'group' as const,
      shareKey: undefined, // groups don't have share keys
    })),
  ];

  return allRecipients;
};

/**
 * Validation schema for the add recipients form
 * @returns The validation schema
 */
export const getValidationSchema = (existingRecipients?: RecipientTag[]) => {
  return Yup.object().shape({
    recipients: Yup.array()
      .min(1, 'At least one recipient is required')
      .test(
        'recipients-valid',
        'Enter a valid email address',
        function (value: MultiInputTag[] | undefined) {
          if (value && value.length > 0) {
            return value.every((recipient: MultiInputTag) =>
              isValidEmail(recipient.value),
            );
          }
          return true;
        },
      )
      .test(
        'recipients-limit',
        'Limit reached: Total recipients cannot exceed 10.',
        function (value: MultiInputTag[] | undefined) {
          const newRecipientsCount = value ? value.length : 0;
          const existingRecipientsCount = existingRecipients
            ? existingRecipients.length
            : 0;
          return newRecipientsCount + existingRecipientsCount <= 10;
        },
      ),
  });
};

/**
 * Custom envelop icon for the add recipients form
 * @returns The custom envelop icon
 */
export const CustomEnvelopIcon = () => <EnvelopIcon />;

/**
 * Get the icon for the recipient type
 * @param type - The type of the recipient
 * @returns The icon for the recipient type
 */
export const getRecipientIcon = (type: RecipientTag['type']) => {
  switch (type) {
    case 'email':
      return <EnvelopIcon />;
    case 'phone':
      return <PhoneIcon viewboxWidth={12} viewboxHeight={14} />;
    case 'contact':
      return <UserIcon height={10} width={10} />;
    case 'group':
      return <UsersIconFilled height={10} width={10} />;
    default:
      return <UserIcon height={10} width={10} />;
  }
};
